import type { CSSProperties, FC } from 'react'
import type { Stage } from '@/constants/home'

import { useRef, useState } from 'react'
import { Heart, MessageCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { hand } from '@/lib/fonts'
import { clamp } from '@/lib/frame'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { Photo } from '@/components/kit/photo'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { stages } from '@/constants/home'

/* -------------------------------- scenes -------------------------------- */

const BriefScene: FC = () => {
    return (
        <div className='relative h-full bg-paper bg-[repeating-linear-gradient(to_bottom,transparent_0,transparent_33px,rgb(0_153_255/0.18)_33px,rgb(0_153_255/0.18)_34px)] p-6 pl-14 md:p-8 md:pl-20'>
            <span className='absolute inset-y-0 left-10 w-px bg-ember/50 md:left-14' />
            <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Brief · Mossline · November</p>
            <div className={cn(hand.className, 'mt-4 space-y-[2px] text-[1.7rem] leading-[34px] text-ink md:text-[2rem]')}>
                <p>Audience: people who keep things for years</p>
                <p>Hunch: repair is the new unboxing</p>
                <p>One job: <span className='marker' data-on='true'>get saved, get sent</span></p>
                <p className='text-ember'>Series? Darning Sundays!!</p>
            </div>
            <span
                className='stamp-in stamp absolute bottom-6 right-6 text-ember'
                data-on='true'
                style={{ '--stamp-rotate': '-9deg', '--stamp-delay': '0.3s' } as CSSProperties}
            >
                Approved
            </span>
        </div>
    )
}

const ShootScene: FC = () => {
    return (
        <div className='relative h-full bg-ink p-4 md:p-5'>
            <div className='relative h-full overflow-hidden rounded-xl'>
                <Photo
                    src='/images/work/fashion-1.jpg'
                    alt=''
                    sizes='(min-width: 1024px) 560px, 90vw'
                    position='50% 22%'
                    className='absolute inset-0'
                />
                <span className='absolute inset-0 bg-black/10' />
                {['left-4 top-4 border-l-2 border-t-2', 'right-4 top-4 border-r-2 border-t-2', 'bottom-4 left-4 border-b-2 border-l-2', 'bottom-4 right-4 border-b-2 border-r-2'].map(corner => (
                    <span
                        key={corner}
                        className={cn('absolute size-8 border-white/90', corner)}
                    />
                ))}
                <span className='absolute left-1/2 top-1/2 size-14 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/70' />
                <p className='absolute left-8 top-7 flex items-center gap-2 font-mono text-xs text-white'>
                    <span className='size-2.5 animate-blink rounded-full bg-[#ff3b30]' />
                    REC
                </p>
                <p className='absolute right-8 top-7 font-mono text-xs text-white'>TAKE 14</p>
                <p className='absolute bottom-7 left-8 font-mono text-xs text-white'>00:00:14:22</p>
                <p className='absolute bottom-7 right-8 font-mono text-xs text-white'>4K · 25fps</p>
            </div>
        </div>
    )
}

const PostScene: FC = () => {
    return (
        <div className='flex h-full flex-col bg-paper'>
            <div className='flex items-center gap-2.5 px-4 py-3'>
                <span className='flex size-8 items-center justify-center rounded-full bg-ink font-heading text-xs font-bold text-cream'>M</span>
                <div className='min-w-0'>
                    <p className='text-sm font-semibold leading-none'>mossline</p>
                    <p className='mt-1 font-mono text-[10px] text-stone'>Scheduled · Thu 18:42</p>
                </div>
                <span className='ml-auto rounded-full bg-ember px-2.5 py-1 font-mono text-[10px] text-ink'>Live soon</span>
            </div>
            <div className='relative min-h-0 flex-1'>
                <Photo
                    src='/images/work/fashion-3.jpg'
                    alt=''
                    sizes='(min-width: 1024px) 380px, 80vw'
                    position='50% 25%'
                    className='absolute inset-0'
                />
                <p className='absolute bottom-3 left-3 rounded-full bg-ink/70 px-3 py-1 font-mono text-[10px] text-cream backdrop-blur'>1 / 6</p>
            </div>
            <div className='px-4 py-3'>
                <div className='flex items-center gap-4 text-ink'>
                    <Heart className='size-5 fill-ember text-ember' />
                    <MessageCircle className='size-5' />
                    <Send className='size-5' />
                    <span className='ml-auto font-mono text-[11px] text-stone'>2,481 likes</span>
                </div>
                <p className='mt-2 text-[13px] leading-snug'>
                    <span className='font-semibold'>mossline</span> Darning Sundays, episode one. Bring us your oldest jumper and we will show you how to save it.
                </p>
            </div>
        </div>
    )
}

const readPoints = 'M0 120 L50 112 L100 104 L150 86 L200 80 L250 56 L300 40 L350 22 L400 10'

const ReadScene: FC = () => {
    return (
        <div
            data-chart=''
            data-revealed=''
            className='flex h-full flex-col bg-paper p-6 md:p-8'
        >
            <div className='flex items-center justify-between'>
                <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Week one read</p>
                <span className='stamp -rotate-6 text-ember'>Sequel: yes</span>
            </div>
            <div className='mt-5 grid grid-cols-3 gap-3'>
                {[
                    { value: '14.2', label: 'replies per 1K views' },
                    { value: '+390%', label: 'saves' },
                    { value: '1,206', label: 'sends to a friend' }
                ].map(stat => (
                    <div
                        key={stat.label}
                        className='rounded-2xl border border-ink/10 p-3'
                    >
                        <p className='font-heading text-[clamp(1.5rem,2.4vw,2.25rem)] font-bold leading-none tracking-[-0.04em]'>{stat.value}</p>
                        <p className='mt-1.5 font-mono text-[10px] text-stone'>{stat.label}</p>
                    </div>
                ))}
            </div>
            <svg
                viewBox='0 0 400 130'
                className='mt-auto h-auto w-full overflow-visible pt-6'
                aria-hidden='true'
            >
                <line x1='0' y1='129' x2='400' y2='129' stroke='rgb(30 30 30 / 0.15)' />
                <path
                    className='draw-path'
                    pathLength={1}
                    d={readPoints}
                    fill='none'
                    stroke='#ff5c00'
                    strokeWidth='3'
                    strokeLinecap='round'
                    style={{ '--draw-delay': '150ms' } as CSSProperties}
                />
            </svg>
        </div>
    )
}

const scenes: Record<Stage['key'], FC> = {
    brief: BriefScene,
    shoot: ShootScene,
    post: PostScene,
    read: ReadScene
}

/* The single frame morphs its shape between stages */
const frames: Record<Stage['key'], CSSProperties> = {
    brief: { width: '100%', borderRadius: '1.25rem', rotate: '-1.5deg' },
    shoot: { width: '100%', borderRadius: '1.75rem', rotate: '0deg' },
    post: { width: '62%', borderRadius: '2.25rem', rotate: '1.5deg' },
    read: { width: '100%', borderRadius: '1.5rem', rotate: '-0.5deg' }
}

/* -------------------------------- section ------------------------------- */

export const HunchToHit: FC = () => {
    const sectionRef = useRef<HTMLElement>(null)
    const [active, setActive] = useState(0)
    const pinned = useMediaQuery('(min-width: 1024px) and (min-height: 640px)')

    useScrollFrame(() => {
        const section = sectionRef.current

        if (!section) return

        const rect = section.getBoundingClientRect()
        const distance = rect.height - window.innerHeight
        const progress = distance > 0 ? clamp(-rect.top / distance) : 0
        const next = Math.min(stages.length - 1, Math.floor(progress * stages.length * 0.999))

        section.style.setProperty('--progress', progress.toFixed(4))
        setActive(current => (current === next ? current : next))
    }, pinned)

    const stage = stages[active]

    return (
        <section
            ref={sectionRef}
            id='hunch-to-hit'
            aria-labelledby='hunch-title'
            className='relative bg-sand pin:h-[380vh]'
        >
            <div className='pin:sticky pin:top-0 pin:flex pin:h-screen pin:flex-col pin:justify-center'>
                <div className='mx-auto w-full max-w-[1400px] px-5 py-20 md:px-10 md:py-28 pin:py-0'>
                    <div className='flex flex-wrap items-end justify-between gap-6'>
                        <div>
                            <SectionLabel index='03'>How it works</SectionLabel>
                            <SplitText
                                as='h2'
                                id='hunch-title'
                                lines={['From hunch to hit']}
                                italic='hit'
                                className='mt-4 text-[clamp(2.75rem,5.6vw,5.5rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                            />
                        </div>
                        <p className='max-w-sm text-base leading-relaxed text-stone'>
                            Four stages, one week, no mystery. Scroll and watch a single idea go from a scribble to a sequel.
                        </p>
                    </div>

                    {pinned ? (
                        <div className='mt-10 grid grid-cols-[1fr_1.35fr] items-center gap-14'>
                            <div>
                                <p className='font-mono text-xs uppercase tracking-[0.16em] text-ember'>
                                    {stage.index} · {stage.stamp}
                                </p>
                                <h3
                                    key={stage.key}
                                    className='pop-up mt-3 text-[clamp(2.25rem,3.6vw,3.5rem)] font-bold leading-none tracking-[-0.04em]'
                                >
                                    {stage.title}
                                </h3>
                                <p
                                    key={`${stage.key}-body`}
                                    className='pop-up mt-4 max-w-md text-lg leading-relaxed text-stone'
                                >
                                    {stage.body}
                                </p>
                            </div>

                            <div className='relative flex h-[min(56vh,520px)] items-center justify-center'>
                                <div
                                    className='relative h-full overflow-hidden border border-ink/10 shadow-[0_40px_80px_-50px_rgb(30_30_30/0.6)] transition-[width,border-radius,rotate] duration-700 ease-expo'
                                    style={frames[stage.key]}
                                >
                                    {stages.map((item, index) => {
                                        const Scene = scenes[item.key]

                                        return (
                                            <div
                                                key={item.key}
                                                aria-hidden={active !== index}
                                                className={cn(
                                                    'absolute inset-0 transition-[opacity,transform,filter] duration-700 ease-expo',
                                                    active === index ? 'scale-100 opacity-100 blur-0' : 'pointer-events-none scale-[0.96] opacity-0 blur-sm'
                                                )}
                                            >
                                                {active === index && <Scene />}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <ol className='mt-12 grid gap-6 md:grid-cols-2'>
                            {stages.map(item => {
                                const Scene = scenes[item.key]

                                return (
                                    <Reveal
                                        as='li'
                                        key={item.key}
                                        className='overflow-hidden rounded-[1.75rem] border border-ink/10 bg-paper'
                                    >
                                        <div className='h-[340px]'>
                                            <Scene />
                                        </div>
                                        <div className='border-t border-ink/10 p-6'>
                                            <p className='font-mono text-xs uppercase tracking-[0.16em] text-ember'>
                                                {item.index} · {item.stamp}
                                            </p>
                                            <h3 className='mt-2 text-3xl font-bold tracking-[-0.04em]'>{item.title}</h3>
                                            <p className='mt-2 text-base leading-relaxed text-stone'>{item.body}</p>
                                        </div>
                                    </Reveal>
                                )
                            })}
                        </ol>
                    )}

                    {pinned && (
                        <ol
                            aria-label='Stages'
                            className='relative mt-12 grid grid-cols-4'
                        >
                            <span
                                aria-hidden='true'
                                className='absolute left-[12.5%] right-[12.5%] top-[7px] h-px bg-ink/15'
                            />
                            <span
                                aria-hidden='true'
                                className='absolute left-[12.5%] right-[12.5%] top-[7px] h-[2px] origin-left bg-ember'
                                style={{ transform: 'scaleX(var(--progress, 0))' }}
                            />
                            {stages.map((item, index) => (
                                <li
                                    key={item.key}
                                    aria-current={active === index ? 'step' : undefined}
                                    className='relative flex flex-col items-center text-center'
                                >
                                    <span
                                        className={cn(
                                            'relative size-[15px] rounded-full border-2 transition-colors duration-500',
                                            index <= active ? 'border-ember bg-ember' : 'border-ink/25 bg-sand'
                                        )}
                                    />
                                    <span className='mt-3 font-mono text-[11px] text-stone'>{item.stamp}</span>
                                    <span className={cn('mt-1 text-sm font-semibold transition-colors', index === active ? 'text-ink' : 'text-ink/45')}>
                                        {item.title}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </div>
        </section>
    )
}
